import { loadProgress, saveProgress } from "@/services/progress";

/**
 * Persist the resumable slice of `flow` after every mutation that changes it.
 *
 * Written out rather than pulled from a persistence library: the interesting parts —
 * which fields to keep, the one-hour expiry, and shedding fields under quota pressure —
 * are all in `services/progress.js` anyway, so a library would only be contributing the
 * `subscribe` call below.
 */

/** Only these fields are small enough (and useful enough) to store. */
const PERSISTED = ["face", "signature", "videoPoster", "hasVideo", "challengeSequence", "resumeAt"];

/** Mutations that change resumable state. Others (e.g. tracking id) need no write. */
const PERSIST_ON = [
  "flow/SET_FACE",
  "flow/SET_SIGNATURE",
  "flow/SET_VIDEO",
  "flow/SET_CHALLENGE",
  "flow/SET_RESUME_AT",
];

function reduce(flowState) {
  return PERSISTED.reduce((acc, key) => {
    acc[key] = flowState[key];
    return acc;
  }, {});
}

export default function persistPlugin(store) {
  store.subscribe((mutation, state) => {
    if (mutation.type === "flow/RESET") return; // clearing is handled by flow/reset
    if (PERSIST_ON.indexOf(mutation.type) === -1) return;
    saveProgress(reduce(state.flow));
  });
}

/**
 * Read a previously saved flow back into the store.
 *
 * Called explicitly at boot rather than from the plugin, because the caller needs to know
 * whether anything was restored in order to decide whether to offer a resume.
 *
 * @returns {boolean} whether any saved progress was found.
 */
export function restoreProgress(store) {
  const saved = loadProgress();
  if (!saved) return false;
  store.commit("flow/HYDRATE", reduce(saved));
  return true;
}
